import { ActionIcon, Checkbox, Group, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { IsoWeek, WeeklyTodo, WeeklyTodoOwner } from "@msc/shared";

import { useCreateTodo, useUpdateTodo } from "../../hooks/useWeekly.js";
import { ApiErrorAlert } from "../ApiErrorAlert.js";
import { istUebertragen, kwLabel } from "../../lib/weekly.js";

export function TodoList({
  weeklyId,
  todos,
  woche,
  readOnly,
  customerId = null,
  prospectId = null,
}: {
  weeklyId: string;
  todos: WeeklyTodo[];
  woche: IsoWeek;
  readOnly: boolean;
  customerId?: string | null;
  prospectId?: string | null;
}) {
  const { t } = useTranslation();
  const create = useCreateTodo(weeklyId);
  const update = useUpdateTodo(weeklyId);
  const [text, setText] = useState("");
  const [owner, setOwner] = useState<WeeklyTodoOwner>("lead");

  const ownerData = [
    { value: "lead", label: t("weekly.ownerLead") },
    { value: "fuehrung", label: t("weekly.ownerLeadership") },
  ];

  const anlegen = () => {
    const sauber = text.trim();
    if (!sauber) return;
    create.mutate({ text: sauber, owner, customerId, prospectId }, { onSuccess: () => setText("") });
  };

  return (
    <Stack gap={4}>
      {todos.map((todo) => {
        const seit = istUebertragen(todo, woche);
        return (
          <Group key={todo.id} gap="xs" wrap="nowrap">
            <Checkbox
              checked={todo.done}
              aria-label={todo.text}
              disabled={readOnly || update.isPending}
              onChange={(e) => update.mutate({ todoId: todo.id, done: e.currentTarget.checked })}
            />
            <TodoText
              todo={todo}
              readOnly={readOnly}
              speichern={(neu, fertig) => update.mutate({ todoId: todo.id, text: neu }, { onSuccess: fertig })}
              speichert={update.isPending}
            />
            <SegmentedControl
              size="xs"
              data={ownerData}
              value={todo.owner}
              disabled={readOnly}
              onChange={(v) => update.mutate({ todoId: todo.id, owner: v as WeeklyTodoOwner })}
            />
            {seit && (
              <Text size="xs" c="dimmed">
                {t("weekly.since", { kw: kwLabel(t, todo.createdIn) })}
              </Text>
            )}
          </Group>
        );
      })}
      {!readOnly && (
        <Group gap="xs" wrap="nowrap">
          <TextInput
            size="xs"
            style={{ flex: 1 }}
            placeholder={t("weekly.addTodo")}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") anlegen();
            }}
          />
          <SegmentedControl size="xs" data={ownerData} value={owner} onChange={(v) => setOwner(v as WeeklyTodoOwner)} />
          <ActionIcon variant="light" onClick={anlegen} loading={create.isPending} aria-label={t("weekly.addTodo")}>
            +
          </ActionIcon>
        </Group>
      )}
      {create.error && <ApiErrorAlert error={create.error} />}
      {update.error && <ApiErrorAlert error={update.error} />}
    </Stack>
  );
}

/**
 * Text eines Todos, im offenen Weekly mit Schreibrecht umbenennbar (#334):
 * Stift-Knopf oder Doppelklick öffnet das Feld, Enter oder Verlassen
 * speichert, Esc verwirft. Ein Todo gibt es je Lead × Bereich nur einmal —
 * der neue Text gilt in jedem offenen Weekly, das es zeigt; abgeschlossene
 * lesen ihren Snapshot.
 */
function TodoText({
  todo,
  readOnly,
  speichern,
  speichert,
}: {
  todo: WeeklyTodo;
  readOnly: boolean;
  speichern: (text: string, fertig: () => void) => void;
  speichert: boolean;
}) {
  const { t } = useTranslation();
  const [entwurf, setEntwurf] = useState<string | null>(null);

  if (entwurf === null) {
    return (
      <Group gap={4} wrap="nowrap" style={{ flex: 1 }}>
        <Text
          size="sm"
          td={todo.done ? "line-through" : undefined}
          onDoubleClick={readOnly ? undefined : () => setEntwurf(todo.text)}
        >
          {todo.text}
        </Text>
        {!readOnly && (
          <ActionIcon
            size="xs"
            variant="subtle"
            color="gray"
            onClick={() => setEntwurf(todo.text)}
            aria-label={t("weekly.editTodo", { text: todo.text })}
          >
            ✎
          </ActionIcon>
        )}
      </Group>
    );
  }

  const uebernehmen = () => {
    if (speichert) return; // Enter speichert schon; das folgende Verlassen nicht noch einmal.
    const sauber = entwurf.trim();
    if (!sauber || sauber === todo.text) {
      setEntwurf(null);
      return;
    }
    speichern(sauber, () => setEntwurf(null));
  };

  return (
    <TextInput
      size="xs"
      style={{ flex: 1 }}
      autoFocus
      maxLength={500}
      value={entwurf}
      readOnly={speichert}
      aria-label={t("weekly.editTodoField", { text: todo.text })}
      onChange={(e) => setEntwurf(e.currentTarget.value)}
      onBlur={uebernehmen}
      onKeyDown={(e) => {
        if (e.key === "Enter") uebernehmen();
        if (e.key === "Escape") setEntwurf(null);
      }}
    />
  );
}
